import { useEffect, useMemo, useRef, useState } from 'react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import ProgressBar from '../../components/common/ProgressBar';
import BarChart from '../../components/common/BarChart';
import InfoTooltip from '../../components/common/InfoTooltip';
import { requisitosDaSubsecao } from '../../utils/catalogo';
import { formatMesAbrev } from '../../utils/date';
import * as catalogoApi from '../../api/catalogo';
import * as conformidadeApi from '../../api/conformidade';
import * as hospitaisApi from '../../api/hospitais';
import * as avaliacoesApi from '../../api/avaliacoes';

const EXPLICACAO_BINARIA =
  'Conta como conforme só o que foi avaliado como Supera ou Conforme. Parcialmente Conforme e Não Conforme contam 0%. Requisitos nunca avaliados também contam como Não Conforme. É a fórmula padrão do produto pra tomada de decisão.';
const EXPLICACAO_PONDERADA =
  'Supera e Conforme contam 100%, Parcialmente Conforme conta 50%, Não Conforme conta 0%. Dá crédito parcial pro que já foi parcialmente resolvido, mas ainda não fechado.';

function ResumoComposicao({ composicao }) {
  if (!composicao || composicao.length === 0) return null;
  return (
    <div className="d-flex flex-wrap gap-2 mt-2">
      {composicao.map((item) => (
        <span key={item.chave} style={{ fontSize: 11.5, color: 'var(--am-cinza-texto-2)' }}>
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: 2,
              background: item.cor,
              marginRight: 4,
            }}
          />
          {item.valor} {item.label}
        </span>
      ))}
    </div>
  );
}

function diasDesde(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(diffMs / 86400000));
}

function agoraDatetimeLocal() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AbaVisaoGeral({ idHospital, hospital, onHospitalAtualizado, somenteLeitura }) {
  const [secoes, setSecoes] = useState(null);
  const [escopo, setEscopo] = useState(null);
  const [avaliacoes, setAvaliacoes] = useState(null);
  const [geralBinaria, setGeralBinaria] = useState(null);
  const [geralPonderada, setGeralPonderada] = useState(null);
  const [porSecao, setPorSecao] = useState({});
  const [historico, setHistorico] = useState([]);
  const [somenteCore, setSomenteCore] = useState(false);
  const [erro, setErro] = useState('');
  const [mostrarSeletorData, setMostrarSeletorData] = useState(false);
  const [dataEscolhida, setDataEscolhida] = useState(agoraDatetimeLocal);
  const [iniciando, setIniciando] = useState(false);
  const [erroIniciar, setErroIniciar] = useState('');
  const tokenBase = useRef(0);
  const tokenConformidade = useRef(0);

  const abrirSeletorData = () => {
    setDataEscolhida(agoraDatetimeLocal());
    setErroIniciar('');
    setMostrarSeletorData(true);
  };

  const confirmarIniciarTrabalho = async () => {
    setIniciando(true);
    setErroIniciar('');
    try {
      const isoUtc = new Date(dataEscolhida).toISOString();
      const { data } = await hospitaisApi.iniciarTrabalho(idHospital, isoUtc);
      onHospitalAtualizado?.(data);
      setMostrarSeletorData(false);
    } catch (err) {
      setErroIniciar(extrairMensagemErro(err, 'Não foi possível iniciar o trabalho.'));
    } finally {
      setIniciando(false);
    }
  };

  // Catálogo/escopo/avaliações só dependem do hospital — não precisam recarregar quando o
  // usuário só troca a fórmula ou o filtro CORE (isso é resolvido no efeito de conformidade).
  const carregarBase = async () => {
    const token = ++tokenBase.current;
    setErro('');
    try {
      const [{ data: hierarquia }, { data: escopoData }, { data: avaliacoesData }] = await Promise.all([
        catalogoApi.listarHierarquia(),
        hospitaisApi.obterEscopo(idHospital),
        avaliacoesApi.listarAtuais(idHospital),
      ]);
      if (tokenBase.current !== token) return; // resposta obsoleta (hospital já trocou)
      setSecoes(hierarquia);
      setEscopo(escopoData);
      setAvaliacoes(avaliacoesData);
    } catch (err) {
      if (tokenBase.current !== token) return;
      setErro(extrairMensagemErro(err, 'Não foi possível carregar a visão geral.'));
    }
  };

  // Binária é o padrão do produto pra tomada de decisão — usada em tudo (geral, por-seção,
  // histórico). Ponderada só existe aqui, como o card "Conformidade Parcial", pra dar contexto
  // de quanto do não-conforme já é parcialmente resolvido. Refaz quando hospital/CORE mudam.
  const carregarConformidade = async () => {
    const token = ++tokenConformidade.current;
    try {
      const [{ data: binariaData }, { data: ponderadaData }, { data: porSecaoData }, { data: historicoData }] =
        await Promise.all([
          conformidadeApi.obter(idHospital, { formula: 'binaria', somenteCore }),
          conformidadeApi.obter(idHospital, { formula: 'ponderada', somenteCore }),
          conformidadeApi.obterPorSecao(idHospital, { formula: 'binaria', somenteCore }),
          conformidadeApi.obterHistorico(idHospital, { formula: 'binaria', somenteCore, meses: 6 }),
        ]);
      if (tokenConformidade.current !== token) return;
      setGeralBinaria(binariaData.percentual);
      setGeralPonderada(ponderadaData.percentual);
      const mapa = {};
      porSecaoData.forEach((linha) => {
        mapa[linha.id_secao] = linha.percentual;
      });
      setPorSecao(mapa);
      setHistorico(historicoData);
    } catch (err) {
      if (tokenConformidade.current !== token) return;
      setErro(extrairMensagemErro(err, 'Não foi possível carregar a conformidade.'));
    }
  };

  const recarregarTudo = () => {
    carregarBase();
    carregarConformidade();
  };

  useEffect(() => {
    carregarBase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idHospital]);

  useEffect(() => {
    carregarConformidade();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idHospital, somenteCore]);

  const cobertura = useMemo(() => {
    if (!secoes || !escopo || !avaliacoes) return null;
    const ativosPorId = new Map(escopo.filter((e) => e.ativo).map((e) => [e.id_requisito, true]));
    const avaliadosPorId = new Set(avaliacoes.map((a) => a.id_requisito));

    const porSecaoCobertura = {};
    secoes.forEach((s) => {
      let requisitosSecao = s.subsecoes.flatMap(requisitosDaSubsecao).filter((r) => ativosPorId.has(r.id_requisito));
      if (somenteCore) requisitosSecao = requisitosSecao.filter((r) => r.core);
      const avaliadosSecao = requisitosSecao.filter((r) => avaliadosPorId.has(r.id_requisito));
      porSecaoCobertura[s.id_secao] = { total: requisitosSecao.length, avaliados: avaliadosSecao.length };
    });
    const total = Object.values(porSecaoCobertura).reduce((acc, c) => acc + c.total, 0);
    const avaliados = Object.values(porSecaoCobertura).reduce((acc, c) => acc + c.avaliados, 0);
    return { total, avaliados, porSecao: porSecaoCobertura };
  }, [secoes, escopo, avaliacoes, somenteCore]);

  const composicao = useMemo(() => {
    if (!avaliacoes || !cobertura) return null;
    const avaliacoesEscopo = somenteCore ? avaliacoes.filter((a) => a.core) : avaliacoes;
    const contagem = { supera: 0, conforme: 0, parcialmente_conforme: 0, nao_conforme: 0, vencido: 0 };
    avaliacoesEscopo.forEach((a) => {
      if (a.status_vigencia === 'vencido') contagem.vencido += 1;
      else if (contagem[a.status] !== undefined) contagem[a.status] += 1;
    });
    const naoAvaliado = Math.max(0, cobertura.total - cobertura.avaliados);
    return [
      { chave: 'supera', label: 'Supera', valor: contagem.supera, cor: 'var(--am-tinta)' },
      { chave: 'conforme', label: 'Conforme', valor: contagem.conforme, cor: 'var(--am-sucesso)' },
      { chave: 'parcialmente_conforme', label: 'Parcialmente conforme', valor: contagem.parcialmente_conforme, cor: 'var(--am-amarelo-acao)' },
      { chave: 'nao_conforme', label: 'Não conforme', valor: contagem.nao_conforme, cor: 'var(--am-critico)' },
      { chave: 'vencido', label: 'Vencido', valor: contagem.vencido, cor: '#a35b3f' },
      { chave: 'nao_avaliado', label: 'Não avaliado', valor: naoAvaliado, cor: 'var(--am-cinza-texto-2)' },
    ].filter((item) => item.valor > 0);
  }, [avaliacoes, cobertura, somenteCore]);

  if (!secoes || geralBinaria === null || !cobertura) {
    return erro ? <ErroCarregamento mensagem={erro} onRetry={recarregarTudo} /> : <LoadingSpinner />;
  }

  return (
    <div>
      {hospital?.data_inicio_trabalho ? (
        <div
          className="card-soft d-flex align-items-center gap-3 mb-3"
          style={{ padding: '14px 20px', background: 'var(--am-amarelo-nevoa)' }}
        >
          <span className="ms" style={{ fontSize: 24, color: 'var(--am-link)' }}>
            task_alt
          </span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--am-tinta)' }}>
              Trabalho iniciado em {new Date(hospital.data_inicio_trabalho).toLocaleString('pt-BR')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)' }}>
              {diasDesde(hospital.data_inicio_trabalho)} dia(s) desde o início
            </div>
          </div>
        </div>
      ) : (
        !somenteLeitura && (
          <div className="mb-3">
            {!mostrarSeletorData ? (
              <button
                type="button"
                className="am-btn am-btn-accent"
                style={{ fontSize: 14, padding: '13px 22px', boxShadow: 'var(--am-shadow-frame)' }}
                onClick={abrirSeletorData}
              >
                <span className="ms">play_arrow</span>
                Iniciar Trabalho
              </button>
            ) : (
              <div
                className="card-soft d-flex align-items-end gap-3 flex-wrap"
                style={{ padding: 18, background: 'var(--am-amarelo-nevoa)' }}
              >
                <label className="am-field" style={{ maxWidth: 240 }}>
                  <div className="am-field__label-row">
                    <span className="am-field__label">Data/hora de início do trabalho</span>
                  </div>
                  <input
                    type="datetime-local"
                    value={dataEscolhida}
                    onChange={(e) => setDataEscolhida(e.target.value)}
                    max={agoraDatetimeLocal()}
                  />
                </label>
                <button
                  type="button"
                  className="am-btn am-btn-accent"
                  onClick={confirmarIniciarTrabalho}
                  disabled={iniciando}
                >
                  <span className="ms">check</span>
                  {iniciando ? 'Salvando...' : 'Confirmar'}
                </button>
                <button
                  type="button"
                  className="am-btn am-btn-outline"
                  onClick={() => setMostrarSeletorData(false)}
                  disabled={iniciando}
                >
                  Cancelar
                </button>
              </div>
            )}
            {erroIniciar && <div className="am-field__error mt-1">{erroIniciar}</div>}
          </div>
        )
      )}

      <div className="filter-tabs mb-3">
        <button
          type="button"
          className={`filter-tab${!somenteCore ? ' is-active' : ''}`}
          onClick={() => setSomenteCore(false)}
        >
          Todos os requisitos
        </button>
        <button
          type="button"
          className={`filter-tab${somenteCore ? ' is-active' : ''}`}
          onClick={() => setSomenteCore(true)}
        >
          Somente CORE
        </button>
      </div>

      <div className="d-flex flex-wrap gap-3 mb-3">
        <div className="card-soft d-flex align-items-center gap-4" style={{ padding: 24, flex: '1 1 320px' }}>
          <div
            style={{
              width: 84,
              height: 84,
              borderRadius: '50%',
              background: 'var(--am-amarelo-nevoa)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              fontWeight: 800,
              color: 'var(--am-tinta)',
              flexShrink: 0,
            }}
          >
            {geralBinaria}%
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--am-tinta)' }}>
              Conformidade Geral{somenteCore ? ' (CORE)' : ''}
              <InfoTooltip texto={EXPLICACAO_BINARIA} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)' }}>
              {cobertura.avaliados} de {cobertura.total} requisitos{somenteCore ? ' CORE' : ''} ativos já avaliados
            </div>
            <ResumoComposicao composicao={composicao} />
          </div>
        </div>

        <div className="card-soft d-flex align-items-center gap-4" style={{ padding: 24, flex: '1 1 320px' }}>
          <div
            style={{
              width: 84,
              height: 84,
              borderRadius: '50%',
              background: 'var(--am-amarelo-nevoa)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              fontWeight: 800,
              color: 'var(--am-tinta)',
              flexShrink: 0,
            }}
          >
            {geralPonderada}%
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--am-tinta)' }}>
              Conformidade Parcial{somenteCore ? ' (CORE)' : ''}
              <InfoTooltip texto={EXPLICACAO_PONDERADA} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)' }}>
              {cobertura.avaliados} de {cobertura.total} requisitos{somenteCore ? ' CORE' : ''} ativos já avaliados
            </div>
            <ResumoComposicao composicao={composicao} />
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-3 mb-3">
        <div className="card-soft" style={{ padding: 24, flex: '1 1 380px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--am-tinta)', marginBottom: 4 }}>
            Comparação por seção{somenteCore ? ' (CORE)' : ''}
          </div>
          <p style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)', margin: '0 0 4px' }}>
            % de conformidade em cada seção do manual ONA (1 a 4)
          </p>
          <BarChart dados={secoes.map((s) => ({ label: `Seção ${s.numero}`, valor: porSecao[s.id_secao] ?? 0 }))} />
        </div>

        {historico.length > 0 && (
          <div className="card-soft" style={{ padding: 24, flex: '1 1 380px' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--am-tinta)', marginBottom: 4 }}>
              Evolução da conformidade{somenteCore ? ' (CORE)' : ''}
            </div>
            <p style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)', margin: '0 0 4px' }}>
              % de conformidade geral, mês a mês
            </p>
            <BarChart
              dados={historico.map((h) => ({ label: formatMesAbrev(h.mes_referencia), valor: h.percentual }))}
              corBarra="var(--am-link)"
            />
          </div>
        )}
      </div>

      {composicao && composicao.length > 0 && (
        <div className="card-soft mb-3" style={{ padding: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--am-tinta)', marginBottom: 12 }}>
            Composição por status{somenteCore ? ' (CORE)' : ''}
          </div>
          <div className="d-flex" style={{ height: 14, borderRadius: 100, overflow: 'hidden', background: 'var(--am-borda-2)' }}>
            {composicao.map((item) => (
              <div
                key={item.chave}
                style={{ width: `${(item.valor / cobertura.total) * 100}%`, background: item.cor }}
                title={`${item.label}: ${item.valor}`}
              />
            ))}
          </div>
          <div className="d-flex flex-wrap gap-3 mt-3">
            {composicao.map((item) => (
              <div key={item.chave} className="d-flex align-items-center gap-2" style={{ fontSize: 12.5, color: 'var(--am-cinza-texto-2)' }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: item.cor, flexShrink: 0 }} />
                {item.label} <strong style={{ color: 'var(--am-tinta)' }}>{item.valor}</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-soft" style={{ padding: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--am-tinta)', marginBottom: 16 }}>
          Conformidade por seção{somenteCore ? ' (CORE)' : ''}
        </div>
        <div className="d-flex flex-column gap-3">
          {secoes.map((s) => {
            const cob = cobertura.porSecao[s.id_secao] || { total: 0, avaliados: 0 };
            return (
              <div key={s.id_secao}>
                <div className="d-flex justify-content-between mb-1" style={{ fontSize: 13 }}>
                  <span style={{ fontWeight: 600, color: 'var(--am-tinta)' }}>
                    Seção {s.numero} — {s.nome}
                  </span>
                  <span style={{ color: 'var(--am-cinza-texto-2)' }}>
                    {porSecao[s.id_secao] ?? 0}%{' '}
                    <span style={{ fontSize: 11 }}>
                      ({cob.avaliados}/{cob.total} avaliados)
                    </span>
                  </span>
                </div>
                <ProgressBar percentual={porSecao[s.id_secao] ?? 0} size="lg" />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
